import {
  FIRST_ITEM_INDEX,
  NAV_ITEMS,
  PROPERTY_DETAILS_TAB_NAMES,
  URL_NAVS,
} from '@/constants';
import * as dashboardActions from '@/dashboard/dashboard-actions';
import { ListingColDefs } from '@/list/column-defs/listing-col-defs';
import { AsyncRsGrid } from '@/list/components/async-rs-grid';
import { AddrCell } from '@/list/components/cells/addr-cell';
import { AddressCell } from '@/list/components/cells/address-cell';
import { GantRowCell } from '@/list/components/cells/gant-row-cell';
import { PdfLinkCell } from '@/list/components/cells/pdf-link-cell';
import { PlatformLinkCell } from '@/list/components/cells/platform-link-cell';
import { StatusCell } from '@/list/components/cells/status-cell';
import { gridRowHeightInteger } from '@/list/list-constants';
import { usePropertyDetails } from '@/utils/hooks/data/usePropertyDetails';
import { listViewCalendarMgr } from '@/utils/listings-data-utils';
import NoResultsMessage from '@/widgets/components/no-results-message';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { UpgradeRequiredMessage } from './upgrade-required-message';

const Wrapper = styled.div`
  display: flex;
  margin-top: 20px;
  width: 100%;
  height: 100%;
  margin-bottom: 32px;
  align-items: center;
  justify-content: center;
`;

const GridWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const frameworkComponents = {
  platformLinkCellRenderer: PlatformLinkCell,
  pdfLinkCellRenderer: PdfLinkCell,
  gantRowCellRenderer: GantRowCell,
  AddressCellRenderer: AddressCell,
  diffStatusCellRenderer: StatusCell,
  licenseStatusCellRenderer: StatusCell,
  addrCellRenderer: AddrCell,
};

const sidebarConf = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        /* left some commented out on purpose to expedite tweaking */
        // suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
        // suppressSideButtons: true,
        // suppressColumnFilter: true,
        // suppressColumnSelectAll: true,
        // suppressColumnExpandAll: true,
      },
    },
    {
      id: 'filters',
      labelDefault: 'Filters',
      labelKey: 'filters',
      iconKey: 'filter',
      toolPanel: 'agFiltersToolPanel',
    },
  ],
};

interface ListViewerProps {
  upgradeRequired: boolean;
  rowData?: Record<string, unknown>[];
  rowDataUpdated?: boolean;
  immutableData?: boolean;
  getRowNodeId?: (params: {
    data: { deckardid?: string; propertyDeckardid?: string };
  }) => string | number;
  columnDefs: unknown[];
  setGridApi: (api: unknown | null) => void;
  defaultColDef?: unknown;
  autoGroupColumnDef?: unknown;
  onFilterChanged?: (event: unknown) => void;
  weekendsInMonth?: unknown;
  inFlight?: boolean;
  onBodyScrollEnd?: (event: unknown) => void;
}

export function ListViewer({
  upgradeRequired,
  rowData,
  rowDataUpdated,
  immutableData,
  getRowNodeId,
  columnDefs,
  setGridApi,
  defaultColDef,
  autoGroupColumnDef,
  onFilterChanged,
  weekendsInMonth,
  inFlight,
  onBodyScrollEnd,
}: ListViewerProps) {
  const { colorGetter } = listViewCalendarMgr;
  const { goToPropertyDetails, propertyDetailsTabs } = usePropertyDetails();

  const dispatch = useDispatch();

  const handleDeckardIDSelect = useCallback((deckardid, tabKey = null) => {
    dispatch(
      dashboardActions.openPropertyByDeckardId([
        NAV_ITEMS.list,
        deckardid,
        tabKey,
      ])
    );
  }, []);

  const onRowClickCB = useCallback(
    (params: { data: { deckardid?: string; propertyDeckardid?: string } }) => {
      /**
       * From the data:
       *  Listings use deckardid for property deckardids
       *  Licenses use propertyDeckardid
       */
      const { deckardid, propertyDeckardid } = params.data || {};

      const dekcardIdToEncode = deckardid || propertyDeckardid;

      // Do nothing when there is no ID present
      if (!dekcardIdToEncode) return;

      goToPropertyDetails({
        propertyDeckardId: dekcardIdToEncode,
        propertyDetailsTab: propertyDetailsTabs?.license?.enabled
          ? PROPERTY_DETAILS_TAB_NAMES.license
          : PROPERTY_DETAILS_TAB_NAMES.overview,
        tab: URL_NAVS.list,
      });
    },
    [goToPropertyDetails, propertyDetailsTabs]
  );

  /** Callback that on cell click checks if cell is a group cell. If so, opens property details view */
  const onCellClicked = useCallback(
    (params: unknown) => {
      // @ts-expect-error - params is untyped
      const { node } = params;
      if (node?.group) {
        handleDeckardIDSelect(
          // Seems like we just trust that there is always a Deckard ID
          // @ts-expect-error - params is untyped
          params.node?.allLeafChildren?.[FIRST_ITEM_INDEX]?.data?.deckardid,
          PROPERTY_DETAILS_TAB_NAMES.overview
        );
      }
    },
    [handleDeckardIDSelect]
  );

  if (upgradeRequired) {
    return <UpgradeRequiredMessage />;
  }

  if (!rowData?.length) {
    return (
      <Wrapper>
        <NoResultsMessage active={Boolean(rowData)} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <GridWrapper>
        <AsyncRsGrid
          rowDataUpdated={rowDataUpdated}
          headerHeight={60}
          groupHeaderHeight={40}
          immutableData={immutableData}
          getRowNodeId={getRowNodeId}
          columnDefs={columnDefs}
          suppressMenuHide={true}
          onGridReady={setGridApi}
          defaultColDef={defaultColDef}
          rowData={rowData}
          columnGroups
          autoGroupColumnDef={autoGroupColumnDef}
          groupDefaultExpanded={-1}
          frameworkComponents={frameworkComponents}
          rowHeight={gridRowHeightInteger}
          rowSelection="multiple"
          suppressRowClickSelection
          suppressContextMenu
          onFilterChanged={onFilterChanged}
          context={{ colorGetter }}
          weekendsInMonth={weekendsInMonth}
          showLoadingOverlay={inFlight}
          onRowClicked={onRowClickCB}
          onCellClicked={onCellClicked}
          excelStyles={ListingColDefs.excelStyles}
          sideBar={sidebarConf}
          tooltipShowDelay={500}
          onBodyScrollEnd={onBodyScrollEnd}
        />
      </GridWrapper>
    </Wrapper>
  );
}
